// Inert tool the chat agent calls when an analysis is worth saving
// as a kernel. Emits suggestion data on `details`; the chat UI mounts
// an inline card with Save / Dismiss actions. Save kicks off the
// existing /save-kernel flow (agentic extraction → test_kernel →
// persist) using the proposed command + title as starting hints.
//
// Two usage patterns:
//
//   A) "save what I just rendered" — the typical path. The agent
//      finishes an analysis ending in a successful render_block, then
//      calls suggest_kernel with just command/title. The sub-agent
//      extractor lifts the render spec from the transcript.
//
//   B) "save what I described" — when the agent has shown the user
//      multiple render options in text and the user picks one, the
//      agent can call suggest_kernel with a `proposedRender` field
//      carrying the full render spec inline. This drops the
//      render_block precondition; the save flow uses the proposed
//      spec instead of looking for one in the transcript.
//
// Why inert: the suggestion shouldn't run side effects on its own.
// The user is the gatekeeper for what becomes a kernel — the tool's
// only job is to make it one click instead of a remembered slash
// command.

import { Type } from "@earendil-works/pi-ai";
import { hasRendered } from "./renderTracker.js";

// Factory: every chat builds its own suggest_kernel pointed at the
// chat's namespace key so the render-precondition lookup hits the
// right tracker entry. Same pattern as createPythonTool /
// createAnalyticsQueryTool.
export function createSuggestKernelTool({ getNamespaceKey } = {}) {
    return {
        name: "suggest_kernel",
        label: "Suggest kernel",
        description:
            "Offer the user a one-click save of an analysis as a saved kernel. Two patterns:\n\n" +
            "Pattern A — save what was just rendered. Call AFTER a successful render_block in this chat. The save flow will lift the render spec from the transcript automatically; pass only `command`, `title`, and optional `reasoning`.\n\n" +
            "Pattern B — save what was described in text. When you've shown the user multiple chart options in prose (without actually rendering them) and the user picks one to keep, call suggest_kernel with the chosen render's full spec in `proposedRender`. This is the natural path for 'build me a kernel for the treemap you described above' — no render_block needed first.\n\n" +
            "When to call:\n" +
            "  • the recipe is general-purpose (parameter values like thresholds, window sizes, top-N counts could plausibly vary),\n" +
            "  • the user is likely to want to rerun this against fresh data later,\n" +
            "  • the result would be useful when scoped (e.g. could show on every endpoint page).\n\n" +
            "Don't suggest for one-off questions, exploration where the result was a dead end, or analyses whose value was the LLM's interpretation rather than the data shape. The user always has /save-kernel directly; suggesting is a nicety, not the only path.\n\n" +
            "Precondition (Pattern A only): at least one successful render_block in this chat. When `proposedRender` is supplied, this precondition is waived — but the chat must still contain analytics_query / execute_python work the kernel can be built from. Without ANY analysis history the save will fail downstream regardless.\n\n" +
            "Output: emits a suggestion card with Save / Dismiss buttons. Save invokes the kernel extractor against the recent transcript with `command` and `title` as starting hints. Does NOT actually save the kernel — that's the user's call.",
        parameters: Type.Object({
            command: Type.String({
                description:
                    "Proposed slash-command name (kebab-case, no leading slash). The user can override at save time; this is just a starting hint. Pick something concise and descriptive — e.g. `top-tools`, `endpoint-leaderboard`, `weekly-rhythm`.",
            }),
            title: Type.String({
                description:
                    "Short human-readable label (3–6 words). Shown on the kernel tab and in the kernels admin view. Do not include surrounding quotes.",
            }),
            reasoning: Type.Optional(
                Type.String({
                    description:
                        "One sentence on why this analysis is reusable / worth saving. Surfaced under the card so the user can decide quickly. Keep it ≤120 chars.",
                }),
            ),
            proposedRender: Type.Optional(
                Type.Unknown({
                    description:
                        "Pattern B only: the full render_block spec (kind + per-kind fields, same shape render_block takes) that the kernel should produce. Use when the user is asking you to save a chart you DESCRIBED in text but did not render. The save flow will use this spec verbatim instead of looking for a prior render_block in the transcript. Omit for Pattern A (save-what-was-rendered).",
                }),
            ),
            agentic: Type.Optional(
                Type.Boolean({
                    description:
                        "DEFAULT FALSE — decide deliberately. A kernel is deterministic (analytics_query + python + mcp_tool) and reproducible. Set this true ONLY when the recipe genuinely needs an in-line reasoning step that python cannot deterministically encode — e.g. choosing among results by meaning ('the most security-relevant cluster'), or crafting a tool argument from fuzzy text. If a python expression could compute it, leave it false. Agentic kernels re-run an LLM on every refresh, so prefer deterministic unless the judgment is the point.",
                }),
            ),
        }),
        async execute(_id, args) {
            const command = sanitizeSuggestionCommand(args?.command);
            const title = stripQuotes(String(args?.title || "")).trim();
            const reasoning = (args?.reasoning || "").trim() || null;
            const proposedRender = isPlainObject(args?.proposedRender) ? args.proposedRender : null;

            if (!command || !title) {
                return {
                    content: [{ type: "text", text: "suggest_kernel needs both command and title" }],
                    details: { error: "missing command or title" },
                    isError: true,
                };
            }

            // Pattern B waives the render-precondition: the agent is
            // attaching the render spec directly, so we don't need
            // evidence of a prior render_block in the chat.
            if (!proposedRender) {
                const ns = getNamespaceKey?.() || "default";
                if (!hasRendered(ns)) {
                    const message =
                        "suggest_kernel requires either (a) a prior successful render_block in this chat, or (b) a `proposedRender` spec inline (Pattern B — use this when the user is asking you to save a chart you described in text but did not render).";
                    return {
                        content: [{ type: "text", text: message }],
                        details: { error: message },
                        isError: true,
                    };
                }
            }

            // Lightweight shape check on proposedRender — full validation
            // happens downstream via test_kernel, but catching an obvious
            // typo (no `kind`) here prevents an opaque sub-agent failure.
            if (proposedRender && typeof proposedRender.kind !== "string") {
                const message = "proposedRender must include a `kind` field naming the render primitive (e.g. \"kind\": \"treemap\").";
                return {
                    content: [{ type: "text", text: message }],
                    details: { error: message },
                    isError: true,
                };
            }

            return {
                content: [{
                    type: "text",
                    text: `suggested /${command} — ${title}${proposedRender ? ` (with proposed ${proposedRender.kind} render)` : ""}`,
                }],
                details: {
                    kind: "kernelSuggestion",
                    command,
                    title,
                    reasoning,
                    agentic: args?.agentic === true,
                    ...(proposedRender ? { proposedRender } : {}),
                },
            };
        },
    };
}

// Back-compat singleton for callers without a chat namespace
// (tests, harnesses). Points at the "default" namespace; the
// precondition will fire only against rendered state in that
// namespace, which the live chat path never shares.
export const suggestKernelTool = createSuggestKernelTool({ getNamespaceKey: () => "default" });

function sanitizeSuggestionCommand(raw) {
    if (!raw) return "";
    return String(raw)
        .trim()
        .replace(/^\//, "")
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
}

function stripQuotes(s) {
    if (!s) return s;
    return s.replace(/^['"](.*)['"]$/s, "$1");
}

function isPlainObject(v) {
    return v != null && typeof v === "object" && !Array.isArray(v);
}
